import { useEffect, useMemo, useState } from "react";
import { Icon } from "./Icon";
import {
  appointmentCalendarFile,
  CALENDAR_FILE_NAME,
  CALENDAR_FILE_TYPE,
  type CalendarAppointment,
  calendarDataUrl,
} from "./lib/calendarFile";

/**
 * "Add to calendar": a real link to a calendar file made in the browser (lead decision L9), so it
 * works the same on a phone. The file is a Blob URL that stays valid while the link is on screen
 * (WebKit drops a download whose URL is revoked straight after the tap); where a Blob URL cannot
 * be made, the link carries the file as a data: address instead. Nothing is stored or sent.
 */
export function AddToCalendar({ appointment }: { readonly appointment: CalendarAppointment }) {
  const { bookingReference, sequence, startAt, endAt, clinicName, clinicAddress } = appointment;
  const file = useMemo(
    () =>
      appointmentCalendarFile({
        bookingReference,
        sequence,
        startAt,
        endAt,
        clinicName,
        clinicAddress,
      }),
    [bookingReference, sequence, startAt, endAt, clinicName, clinicAddress],
  );
  const [objectUrl, setObjectUrl] = useState<string | null>(null);
  useEffect(() => {
    if (typeof Blob === "undefined" || typeof URL.createObjectURL !== "function") return;
    const url = URL.createObjectURL(new Blob([file], { type: CALENDAR_FILE_TYPE }));
    setObjectUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [file]);
  return (
    <div className="calendar-link">
      <a
        className="outline-button booking-link-button"
        href={objectUrl ?? calendarDataUrl(file)}
        download={CALENDAR_FILE_NAME}
      >
        <Icon name="clock" />
        Add to calendar
      </a>
      <small>
        Saves a calendar file. If your calendar doesn't open it, add the date and time yourself.
      </small>
    </div>
  );
}
